import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import {
  type AdminListUserViewStatesRequest,
  type AdminUserViewState,
  useAdminDeleteUserViewStateMutation,
  useAdminDismissUserViewStateMutation,
  useAdminListUserViewStatesQuery,
  useAdminResetUserViewStateMutation,
} from '../api/dismissableViewsApi';

type DismissedFilter = 'all' | 'dismissed' | 'visible';

type DismissableViewsPanelProps = {
  personId: string;
};

function parseErrorMessage(error: unknown): string {
  if (!error) return 'Request failed. Please try again.';
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null) {
    const maybeStatus = error as { status?: number; data?: any; error?: string };
    if (maybeStatus.data && typeof maybeStatus.data === 'object') {
      const data = maybeStatus.data as Record<string, unknown>;
      if (typeof data.error_description === 'string' && data.error_description.trim().length > 0) {
        return data.error_description;
      }
      if (typeof data.message === 'string' && data.message.trim().length > 0) {
        return data.message;
      }
      if (typeof data.error === 'string' && data.error.trim().length > 0) {
        return data.error;
      }
    }
    if (typeof maybeStatus.error === 'string' && maybeStatus.error.trim().length > 0) {
      return maybeStatus.error;
    }
  }
  return 'Request failed. Please try again.';
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export default function DismissableViewsPanel({ personId }: DismissableViewsPanelProps) {
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState<string>('');
  const [dismissed, setDismissed] = useState<DismissedFilter>('all');
  const [page, setPage] = useState(1);
  const per = 50;

  const dismissedParam: boolean | undefined =
    dismissed === 'dismissed' ? true : dismissed === 'visible' ? false : undefined;

  const queryArg: AdminListUserViewStatesRequest = {
    personId,
    q: q || undefined,
    dismissed: dismissedParam,
    page,
    per,
  };

  const { data, error, isLoading, isFetching, refetch } = useAdminListUserViewStatesQuery(
    queryArg,
    {
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );

  const [resetViewState, { isLoading: isResetting }] = useAdminResetUserViewStateMutation();
  const [dismissViewState, { isLoading: isDismissing }] = useAdminDismissUserViewStateMutation();
  const [deleteViewState, { isLoading: isDeleting }] = useAdminDeleteUserViewStateMutation();

  const [busyId, setBusyId] = useState<string | null>(null);
  const items = data?.items ?? [];

  const totalPages = useMemo(() => {
    if (!data?.total) return 1;
    return Math.max(1, Math.ceil(data.total / per));
  }, [data?.total]);

  const formError = error ? parseErrorMessage(error) : null;

  const handleApply = (evt: React.FormEvent) => {
    evt.preventDefault();
    setPage(1);
    setQ(qInput.trim());
  };

  const isBusy = (id: string) =>
    busyId === id || isResetting || isDismissing || isDeleting || isFetching;

  const handleReset = async (row: AdminUserViewState) => {
    if (!window.confirm(`Reset "${row.key}" for this user (show again)?`)) return;
    setBusyId(row.id);
    try {
      await resetViewState({ personId, id: row.id }).unwrap();
    } catch (err) {
      console.error('Failed to reset view state', err);
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (row: AdminUserViewState) => {
    if (!window.confirm(`Force-dismiss "${row.key}" for this user (hide now)?`)) return;
    setBusyId(row.id);
    try {
      await dismissViewState({ personId, id: row.id }).unwrap();
    } catch (err) {
      console.error('Failed to dismiss view state', err);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row: AdminUserViewState) => {
    if (!window.confirm(`Delete "${row.key}" row for this user?`)) return;
    setBusyId(row.id);
    try {
      await deleteViewState({ personId, id: row.id }).unwrap();
    } catch (err) {
      console.error('Failed to delete view state', err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Inspect and correct persisted per-user dismissable view state for this coach.
      </Typography>

      {formError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formError}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Box
          component="form"
          onSubmit={handleApply}
          sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <TextField
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Filter keys (substring)"
            size="small"
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" />,
            }}
          />
          <Select
            size="small"
            value={dismissed}
            onChange={(e) => {
              setPage(1);
              setDismissed(e.target.value as DismissedFilter);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="dismissed">Dismissed</MenuItem>
            <MenuItem value="visible">Not dismissed</MenuItem>
          </Select>

          <Button
            type="submit"
            variant="contained"
            startIcon={<SearchIcon />}
            disabled={isFetching}
          >
            Apply
          </Button>
          <Button
            type="button"
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={isFetching}
            onClick={() => refetch()}
          >
            Refresh
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            Page {page} of {totalPages} • {data?.total ?? 0} keys
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }} variant="outlined">
        {items.length === 0 && !isLoading && !isFetching && !error ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No view states found for this coach.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 500, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Dismissed</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => {
                  const rowBusy = isBusy(row.id);
                  const dismissedLabel = row.dismissed_at ? formatDateTime(row.dismissed_at) : '—';
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {row.key}
                      </TableCell>
                      <TableCell>{dismissedLabel}</TableCell>
                      <TableCell>{formatDateTime(row.updated_at)}</TableCell>
                      <TableCell>{formatDateTime(row.created_at)}</TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'flex-end',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RestartAltIcon />}
                            disabled={rowBusy}
                            onClick={() => handleReset(row)}
                          >
                            Reset
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityOffIcon />}
                            disabled={rowBusy}
                            onClick={() => handleDismiss(row)}
                          >
                            Dismiss
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteIcon />}
                            disabled={rowBusy}
                            onClick={() => handleDelete(row)}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {totalPages > 1 && (
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}
          >
            <Button
              variant="outlined"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              disabled={page >= totalPages || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
